import { FC, ReactNode, useState } from 'react';
import { Collapse } from '@mantine/core';
import { MdOutlineExpandLess, MdOutlineExpandMore } from 'react-icons/md';
import clsx from 'clsx';
import './Box.scss';

export interface IBoxProps {
    /** Элемент */
    children: ReactNode;
    /** Заголовок */
    header?: ReactNode;
    /** ИД */
    id?: string;
    /** Во всю ширину */
    fullWidth?: boolean;
    /** Класс */
    className?: string;
    /** Класс для заголовка (.box-title) */
    headerClassName?: string;
    /** Свернут по умолчанию */
    defaultCollapsed?: boolean;
    /**
     * Возможность сворачивания
     * @default false
     */
    collapseEnabled?: boolean;
    /**
     * Заголовок-подпись: мелкий приглушённый текст вместо заголовка карточки.
     * @default false
     */
    titleAsLabel?: boolean;
    /** Data-атрибуты корня (метки приложения для навигации к блоку). */
    dataAttributes?: Record<`data-${string}`, string | undefined>;
}

// Сворачиваемая панель-карточка. Сворачивание контента — Mantine Collapse:
// честная анимация высоты вместо CSS-хака height:0/visibility; классы box-container/
// box-title/box-content сохранены (внешние SCSS форм логина/виджетов держатся за них),
// контент остаётся смонтированным в свёрнутом виде, как в легаси.
const Box: FC<IBoxProps> = ({
    children,
    header = undefined,
    id = undefined,
    fullWidth = false,
    className = '',
    headerClassName,
    defaultCollapsed = false,
    collapseEnabled = false,
    titleAsLabel = false,
    dataAttributes,
    ...props
}: IBoxProps) => {
    const [collapsed, setCollapsed] = useState<boolean>(defaultCollapsed);

    const width100 = fullWidth ? 'box-container-width100' : '';

    const isCollapsed = collapseEnabled && collapsed;

    return (
        <div
            className={clsx(
                `box-container`,
                !!className && className,
                !!width100 && width100,
                isCollapsed && 'box-container--collapsed',
                titleAsLabel && 'box-container--label',
            )}
            data-testid={id ? `block-${id}` : undefined}
            {...dataAttributes}
            {...props}
        >
            {header !== undefined && (
                <div
                    className={clsx('box-title', titleAsLabel && 'box-title--label', headerClassName)}
                    onClick={() => {
                        if (!collapseEnabled) return;
                        setCollapsed(!collapsed);
                    }}
                >
                    {header}
                    {collapseEnabled && (
                        <button className="box-title__icon" type="button" tabIndex={-1}>
                            {collapsed ? <MdOutlineExpandMore size="20" /> : <MdOutlineExpandLess size="20" />}
                        </button>
                    )}
                </div>
            )}
            <Collapse className="box-collapse" expanded={!isCollapsed}>
                <div className={clsx('box-content', titleAsLabel && 'box-content--label')}>{children}</div>
            </Collapse>
        </div>
    );
};

export default Box;
